import React, { useState, FormEvent } from 'react';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Icons } from '../../components/common/Icons';
import { authApi } from '../../api/authApi';
import './Login.css';

interface ChangePasswordModalProps {
  isOpen: boolean;
  tempPassword: string;
  onSuccess: () => void;
}

export function ChangePasswordModal({ isOpen, tempPassword, onSuccess }: ChangePasswordModalProps) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate new password
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Check passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Make sure new password is different from temp password
    if (newPassword === tempPassword) {
      setError('New password must be different from temporary password');
      return;
    }

    try {
      setLoading(true);
      await authApi.changePassword(tempPassword, newPassword);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="change-password-modal">
        <div className="modal-header">
          <div className="modal-icon">
            <Icons.Lock />
          </div>
          <h2>Change Your Password</h2>
          <p>For security reasons, you must change your temporary password before continuing.</p>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && (
            <div className="login-error">
              <Icons.AlertCircle />
              <span>{error}</span>
            </div>
          )}

          <div className="password-field">
            <Input
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              leftIcon={<Icons.Lock />}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? <Icons.EyeOff /> : <Icons.Eye />}
            </button>
          </div>

          <div className="password-field">
            <Input
              label="Confirm New Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              leftIcon={<Icons.Lock />}
              autoComplete="new-password"
            />
          </div>

          <div className="password-requirements">
            <p>Password must contain:</p>
            <ul>
              <li className={newPassword.length >= 8 ? 'valid' : ''}>
                <Icons.Check /> At least 8 characters
              </li>
              <li className={/[A-Z]/.test(newPassword) ? 'valid' : ''}>
                <Icons.Check /> One uppercase letter
              </li>
              <li className={/[a-z]/.test(newPassword) ? 'valid' : ''}>
                <Icons.Check /> One lowercase letter
              </li>
              <li className={/[0-9]/.test(newPassword) ? 'valid' : ''}>
                <Icons.Check /> One number
              </li>
              <li className={/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? 'valid' : ''}>
                <Icons.Check /> One special character
              </li>
            </ul>
          </div>

          <Button
            type="submit"
            fullWidth
            size="lg"
            isLoading={loading}
            disabled={!newPassword || !confirmPassword}
          >
            Change Password
          </Button>
        </form>
      </div>
    </div>
  );
}

export default ChangePasswordModal;
